import React from "react";
import { cn } from "@/lib/utils";

interface AnswerCapsuleProps {
  term: string;
  definition: string;
  statistic: string;
  source: string;
  className?: string; // For styling
}

/**
 * AnswerCapsule: A component optimized for Generative Engine Optimization (GEO).
 * Renders a structured "Direct Answer" block that is easy for AI to parse.
 * Visible to users as a "Key Takeaway" box.
 */
export function AnswerCapsule({
  term,
  definition,
  statistic,
  source,
  className,
}: AnswerCapsuleProps) {
  return (
    <div
      className={cn(
        "my-8 p-6 bg-secondary/5 border-l-4 border-secondary rounded-r-lg",
        "prose-p:m-0 prose-headings:m-0",
        className
      )}
      itemScope
      itemType="https://schema.org/DefinedTerm"
    >
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-secondary flex items-center gap-2">
          <span className="uppercase text-xs tracking-wider opacity-70">
            Quick Answer:
          </span>
          <span itemProp="name">{term}</span>
        </h2>
        <p className="text-foreground/90 leading-relaxed text-base">
          <span itemProp="description">{definition}</span>
        </p>
        <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4">
          <span className="font-medium text-secondary">
            <strong>Stat:</strong> {statistic}
          </span>
          <span className="italic opacity-80">
            (Source: {source})
          </span>
        </div>
      </div>
    </div>
  );
}
